import { mountStoreDevtool } from "simple-zustand-devtools";
import { setAnalyticsIdentity } from "utils/analytics";
import { config } from "utils/config";
import { setPostHogCompany } from "utils/posthog";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

interface CompanyState {
	id: number | null;
	setCompany: (id: number | null) => void;
	reset: () => void;
}

const initialState = {
	id: null,
};

/**
 * The company id to report to analytics, or `null` for "no company".
 *
 * 🪤 Some callers hand `setCompany` a whole company object rather than an id
 * (see pages/setup/*), which is a pre-existing type lie this change does not
 * fix — and `setCompany` persists whatever it was handed, so the rehydrated
 * value can be an object too. Read the id defensively so the analytics property
 * is a number or nothing — never "[object Object]".
 */
const readCompanyId = (id: unknown): number | null =>
	typeof id === "number"
		? id
		: typeof (id as any)?.id === "number"
			? (id as any).id
			: null;

const useCompanyStore = create<CompanyState>()(
	persist(
		(set) => ({
			...initialState,
			setCompany: (id: number | null) => {
				const companyId = readCompanyId(id);
				setPostHogCompany(companyId);
				// `setPostHogCompany` sets a PostHog PERSON property, which is not
				// visible to a query over event properties — the activation funnel
				// filters on `properties.companyId`. This puts the same id on the
				// event itself, for every `trackActivation` call that follows.
				setAnalyticsIdentity({ companyId });
				set(() => ({ id }));
			},
			reset: () => {
				// Clearing `id` alone left the departed company on the identity, so
				// the next events in this tab were attributed to a company the user
				// is no longer in — worse than no attribution (EQU-738).
				setAnalyticsIdentity({ companyId: null });
				set(initialState);
			},
		}),
		{
			name: "company-storage",
			storage: createJSONStorage(() => sessionStorage),
			// 🪤 `persist` restores `id` by writing state directly — `setCompany`
			// never runs — so after a reload the store knew the company and the
			// analytics identity did not, and every `trackActivation` call until
			// the user re-picked a company shipped without `companyId` (EQU-738).
			// Read the id off the restored state handed to this callback rather
			// than a closure, so a mid-session rehydrate reports what is actually
			// in storage.
			//
			// Nothing to restore is left alone rather than cleared: hydration runs
			// as this module is first evaluated, when `identity` is still empty,
			// so there is no stale company to clear and this stays a no-op on a
			// fresh tab. Leaving a company is handled where it happens, by
			// `setCompany(null)` and `reset()` below.
			onRehydrateStorage: () => (state) => {
				const companyId = readCompanyId(state?.id);
				if (companyId !== null) setAnalyticsIdentity({ companyId });
			},
		},
	),
);

const setCompanyStore = (_: string) => {
	// useCompanyStore.persist.setOptions({
	//   name: `company-storage-${userUid}`,
	// })
};

if (config.isLocal && config.isDev) {
	mountStoreDevtool("Auth", useCompanyStore);
}

export { setCompanyStore, useCompanyStore };
